import { Api } from "./Api";
import _ from "lodash";
import axios from "axios";
import { Employe } from "../models/Employe";
import { Option } from "../models/Option";
import Absence = Employe.Absence;
import Contrat = Employe.Contrat;
import Avenant = Employe.Avenant;
import Planning = Employe.Planning;
import { DateUtils } from "../utils/DateUtils";
import moment from "moment";
import { Trajet } from "../models/Trajet";
import Distance = Trajet.Distance;
import Vehicule = Employe.Vehicule;
import NoteDeFrais = Employe.NoteDeFrais;
import AcquisitionConges = Employe.AcquisitionConges;
import { Contact } from "../models/Contact";

export class EmployeApi extends Api {
    constructor() {
        super("employe");
    }

    getAll(params?: Object): Promise<Array<Employe>> {
        // shared : liste de référence (sélecteurs, filtres) → réponse partagée
        // entre composants pendant une minute.
        const options: any = _.assign({}, params);
        const shared = !!options.shared;
        delete options.shared;

        const request = shared
            ? this.getResourcesShared(options)
            : this.getResources(options);

        return request.then(function (response) {
            let res: Array<Employe> = [];
            if (response.data && response.data.data) {
                res = _.map(response.data.data, function (e) {
                    return new Employe(e);
                });
            }
            return res;
        });
    }

    get(id: Number, params?: Object): Promise<Employe> {
        return this.getResource(id, params).then(function (response) {
            return new Employe(response.data.data);
        });
    }

    save(employe: Employe, params?: Object): Promise<Employe> {
        return super
            .saveResource(employe.serialize(), params)
            .then(function (response) {
                return new Employe(response.data.data);
            });
    }

    // Plannings
    getPlannings(
        employe_id: Number,
        params?: Object
    ): Promise<Array<Planning>> {
        return axios
            .get(this.apiUrl + employe_id + "/plannings", { params: params })
            .then(function (response) {
                let res: Array<Planning> = [];
                res = _.map(response.data.data, function (p) {
                    return new Planning(p);
                });
                return res;
            });
    }

    getPlanning(
        employe_id: Number,
        planning_id: Number,
        params?: Object
    ): Promise<Planning> {
        return axios
            .get(this.apiUrl + employe_id + "/plannings/" + planning_id, {
                params: params,
            })
            .then(function (response) {
                return new Planning(response.data.data);
            });
    }

    getPlanningByDate(
        employe_id: Number,
        date: string,
        params?: Object
    ): Promise<Planning> {
        return axios
            .get(this.apiUrl + employe_id + "/plannings/date/" + date, {
                params: params,
            })
            .then(function (response) {
                let res: Planning = new Planning(response.data.data);
                return res;
            });
    }

    addPlanning(employe_id: Number, planning: Planning): Promise<Planning> {
        return axios
            .post(this.apiUrl + employe_id + "/plannings", planning)
            .then(function (response) {
                return new Planning(response.data.data);
            });
    }

    updatePlanning(employe_id: Number, planning: Planning): Promise<Planning> {
        return axios
            .put(
                this.apiUrl + employe_id + "/plannings/" + planning.id,
                planning
            )
            .then(function (response) {
                return new Planning(response.data.data);
            });
    }

    savePlanning(employe_id: Number, planning: Planning): Promise<Planning> {
        if (planning.id) {
            return this.updatePlanning(employe_id, planning);
        } else {
            return this.addPlanning(employe_id, planning);
        }
    }

    /**
     * La suppression envoie le planning à la corbeille : la raison est
     * obligatoire (cf. core/Modals/ConfirmSuppression).
     */
    removePlanning(
        employe_id: Number,
        planning_id: Number,
        raison: string
    ): Promise<Planning> {
        return axios
            .delete(this.apiUrl + employe_id + "/plannings/" + planning_id, {
                data: { raison: raison },
            })
            .then(function (response) {
                return new Planning(response.data.data);
            });
    }

    // Absences
    getAbsencesAlerts(params?: Object): Promise<number> {
        return axios
            .get(this.baseUrl + "absences/alert", { params: params })
            .then(function (response) {
                return response.data;
            });
    }

    getAllAbsences(params?: Object): Promise<Api.CallResponse> {
        params = _.assign(params, { withoutDuration: true });
        return axios
            .get(this.baseUrl + "absences", { params: params })
            .then(function (response) {
                let res: Array<Absence> = [];
                res = _.map(response.data.data, function (a) {
                    return new Absence(a);
                });
                return {
                    data: res,
                    metadata: response.data.metadata,
                    paginator: response.data.paginator,
                };
            });
    }

    getAbsences(employe_id: Number, params?: Object): Promise<Array<Absence>> {
        return axios
            .get(this.apiUrl + employe_id + "/absences", { params: params })
            .then(function (response) {
                let res: Array<Absence> = [];
                res = _.map(response.data.data, function (a) {
                    return new Absence(a);
                });
                return res;
            });
    }

    getAbsence(
        employe_id: Number,
        absence_id: Number,
        params?: Object
    ): Promise<Absence> {
        return axios
            .get(this.apiUrl + employe_id + "/absences/" + absence_id, {
                params: params,
            })
            .then(function (response) {
                return new Absence(response.data.data);
            });
    }

    getAbsenceContacts(
        absence_id: Number,
        params?: Object
    ): Promise<Array<Contact>> {
        return axios
            .get(this.baseUrl + "absences/" + absence_id + "/contacts", {
                params: params,
            })
            .then(function (response) {
                let res: Array<Contact> = [];
                res = _.map(response.data.data, function (c) {
                    return new Contact(c);
                });
                return res;
            });
    }

    protected addAbsence(
        employe_id: Number,
        absence: Absence
    ): Promise<Absence> {
        return axios
            .post(this.apiUrl + employe_id + "/absences", absence)
            .then(function (response) {
                return new Absence(response.data.data);
            });
    }

    protected updateAbsence(
        employe_id: Number,
        absence: Absence
    ): Promise<Absence> {
        return axios
            .put(this.apiUrl + employe_id + "/absences/" + absence.id, absence)
            .then(function (response) {
                return new Absence(response.data.data);
            });
    }

    saveAbsence(employe_id: Number, absence: Absence): Promise<Absence> {
        if (absence.id) {
            return this.updateAbsence(employe_id, absence);
        } else {
            return this.addAbsence(employe_id, absence);
        }
    }

    /**
     * La suppression envoie l'absence à la corbeille : la raison est
     * obligatoire (cf. core/Modals/ConfirmSuppression).
     */
    removeAbsence(
        employe_id: Number,
        absence_id: Number,
        raison: string
    ): Promise<Absence> {
        return axios
            .delete(this.apiUrl + employe_id + "/absences/" + absence_id, {
                data: { raison: raison },
            })
            .then(function (response) {
                return new Absence(response.data.data);
            });
    }

    getAcquisitionConges(
        employe_id: Number,
        year: Number,
        params?: Object
    ): Promise<AcquisitionConges> {
        return axios
            .get(this.apiUrl + employe_id + "/acquisition_conges/" + year, {
                params: params,
            })
            .then(function (response) {
                return new AcquisitionConges(response.data.data);
            });
    }

    setAcquisitionCongesInitialValue(
        employe_id: Number,
        year: Number,
        value: Number,
        params?: Object
    ): Promise<AcquisitionConges> {
        return axios
            .post(
                this.apiUrl +
                    employe_id +
                    "/acquisition_conges/" +
                    year +
                    "/" +
                    value,
                {
                    params: params,
                }
            )
            .then(function (response) {
                return new AcquisitionConges(response.data.data);
            });
    }

    // Contrats
    getContrats(employe_id: Number, params?: Object): Promise<Array<Contrat>> {
        return axios
            .get(this.apiUrl + employe_id + "/contrats", { params: params })
            .then(function (response) {
                let res: Array<Contrat> = [];
                res = _.map(response.data.data, function (c) {
                    return new Contrat(c);
                });
                return res;
            });
    }

    getContrat(
        employe_id: Number,
        contrat_id: Number,
        params?: Object
    ): Promise<Contrat> {
        return axios
            .get(this.apiUrl + employe_id + "/contrats/" + contrat_id, {
                params: params,
            })
            .then(function (response) {
                return new Contrat(response.data.data);
            });
    }

    protected addContrat(
        employe_id: Number,
        contrat: Contrat
    ): Promise<Contrat> {
        return axios
            .post(this.apiUrl + employe_id + "/contrats", contrat)
            .then(function (response) {
                return new Contrat(response.data.data);
            });
    }

    protected updateContrat(
        employe_id: Number,
        contrat: Contrat
    ): Promise<Contrat> {
        return axios
            .put(this.apiUrl + employe_id + "/contrats/" + contrat.id, contrat)
            .then(function (response) {
                return new Contrat(response.data.data);
            });
    }

    saveContrat(employe_id: Number, contrat: Contrat): Promise<Contrat> {
        if (contrat.id) {
            return this.updateContrat(employe_id, contrat);
        } else {
            return this.addContrat(employe_id, contrat);
        }
    }

    /**
     * La suppression envoie le contrat (et ses avenants) à la corbeille :
     * la raison est obligatoire (cf. core/Modals/ConfirmSuppression).
     */
    removeContrat(
        employe_id: Number,
        contrat_id: Number,
        raison: string
    ): Promise<Contrat> {
        return axios
            .delete(this.apiUrl + employe_id + "/contrats/" + contrat_id, {
                data: { raison: raison },
            })
            .then(function (response) {
                return new Contrat(response.data.data);
            });
    }

    // Avenants
    getAvenants(
        employe_id: Number,
        contrat_id: Number,
        params?: Object
    ): Promise<Array<Avenant>> {
        return axios
            .get(
                this.apiUrl +
                    employe_id +
                    "/contrats/" +
                    contrat_id +
                    "/avenants",
                {
                    params: params,
                }
            )
            .then(function (response) {
                let res: Array<Avenant> = [];
                res = _.map(response.data.data, function (a) {
                    return new Avenant(a);
                });
                return res;
            });
    }

    getAvenant(
        employe_id: Number,
        contrat_id: Number,
        avenant_id: Number,
        params?: Object
    ): Promise<Avenant> {
        return axios
            .get(
                this.apiUrl +
                    employe_id +
                    "/contrats/" +
                    contrat_id +
                    "/avenants/" +
                    avenant_id,
                { params: params }
            )
            .then(function (response) {
                return new Avenant(response.data.data);
            });
    }

    protected addAvenant(
        employe_id: Number,
        contrat_id: Number,
        avenant: Avenant
    ): Promise<Avenant> {
        return axios
            .post(
                this.apiUrl +
                    employe_id +
                    "/contrats/" +
                    contrat_id +
                    "/avenants",
                avenant
            )
            .then(function (response) {
                return new Avenant(response.data.data);
            });
    }

    protected updateAvenant(
        employe_id: Number,
        contrat_id: Number,
        avenant: Avenant
    ): Promise<Avenant> {
        return axios
            .put(
                this.apiUrl +
                    employe_id +
                    "/contrats/" +
                    contrat_id +
                    "/avenants/" +
                    avenant.id,
                avenant
            )
            .then(function (response) {
                return new Avenant(response.data.data);
            });
    }

    saveAvenant(
        employe_id: Number,
        contrat_id: Number,
        avenant: Avenant
    ): Promise<Avenant> {
        if (avenant.id) {
            return this.updateAvenant(employe_id, contrat_id, avenant);
        } else {
            return this.addAvenant(employe_id, contrat_id, avenant);
        }
    }

    removeAvenant(
        employe_id: Number,
        contrat_id: Number,
        avenant_id: Number,
        raison: string
    ): Promise<Avenant> {
        return axios
            .delete(
                this.apiUrl +
                    employe_id +
                    "/contrats/" +
                    contrat_id +
                    "/avenants/" +
                    avenant_id,
                { data: { raison: raison } }
            )
            .then(function (response) {
                return new Avenant(response.data.data);
            });
    }

    getDistances(
        employeId: number,
        month: DateUtils.Month,
        params?: Object
    ): Promise<Array<Distance>> {
        let date = moment(month.year + "-" + month.month, "YYYY-MM");
        let startDate = date
            .clone()
            .date(20)
            .subtract(1, "month")
            .format("YYYY-MM-DD");
        let endDate = date.date(19).format("YYYY-MM-DD");
        return axios
            .get(
                this.apiUrl +
                    employeId +
                    "/trajet/" +
                    startDate +
                    "/" +
                    endDate,
                { params: params }
            )
            .then(function (response) {
                let res: Array<Distance> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (d) {
                        return new Distance(d);
                    });
                }
                return res;
            });
    }

    getVehicule(employeId: number, params?: Object): Promise<Vehicule> {
        return axios
            .get(this.apiUrl + employeId + "/vehicule", { params: params })
            .then(function (response) {
                return new Vehicule(response.data.data);
            });
    }

    saveVehicule(employeId: number, vehicule: Vehicule): Promise<Vehicule> {
        return axios
            .put(this.apiUrl + employeId + "/vehicule", vehicule)
            .then(function (response) {
                return new Vehicule(response.data.data);
            });
    }

    getNotesDeFrais(employeId: number): Promise<Array<NoteDeFrais>> {
        return axios
            .get(this.apiUrl + employeId + "/note_de_frais")
            .then(function (response) {
                let res: Array<NoteDeFrais> = [];
                if (response.data && response.data.data) {
                    res = _.map(response.data.data, function (d) {
                        return new NoteDeFrais(d);
                    });
                }
                return res;
            });
    }

    /** Notes de frais d'un mois pour tous les employés, par id d'employé. */
    private static notesDeFraisByMonth: Map<
        string,
        { loadedAt: number; promise: Promise<Map<number, any>> }
    > = new Map();

    private static readonly NOTES_DE_FRAIS_TTL = 20 * 1000;

    getNoteDeFrais(
        employeId: number,
        month: DateUtils.Month
    ): Promise<NoteDeFrais> {
        // Une requête groupée par mois, partagée par les trois composants de
        // chaque ligne de la page des frais (auparavant trois requêtes par
        // employé, puis une toutes les 30 s par ligne).
        let date = moment(month.year + "-" + month.month, "YYYY-MM");
        const key = date.format("YYYY-MM");

        let entry = EmployeApi.notesDeFraisByMonth.get(key);
        if (
            !entry ||
            Date.now() - entry.loadedAt > EmployeApi.NOTES_DE_FRAIS_TTL
        ) {
            const promise = axios
                .get(this.apiUrl, {
                    params: {
                        include: "note_de_frais",
                        month: key,
                        showArchived: true,
                    },
                })
                .then(function (response) {
                    const notes = new Map<number, any>();
                    _.forEach(response.data.data, function (employe) {
                        if (employe.note_de_frais) {
                            notes.set(employe.id, employe.note_de_frais);
                        }
                    });
                    return notes;
                })
                .catch(function (error) {
                    EmployeApi.notesDeFraisByMonth.delete(key);
                    throw error;
                });
            entry = { loadedAt: Date.now(), promise: promise };
            EmployeApi.notesDeFraisByMonth.set(key, entry);
        }

        return entry.promise.then(function (notes) {
            const data = notes.get(Number(employeId));
            if (!data) {
                // Même comportement que le 404 de l'endpoint unitaire.
                throw new Error("Note de frais introuvable");
            }
            return new NoteDeFrais(data);
        });
    }

    static forgetNotesDeFrais(): void {
        EmployeApi.notesDeFraisByMonth.clear();
    }

    saveNoteDeFrais(
        employeId: number,
        noteDeFrais: NoteDeFrais
    ): Promise<NoteDeFrais> {
        let date = moment(
            noteDeFrais.month.year + "-" + noteDeFrais.month.month,
            "YYYY-MM"
        );
        EmployeApi.forgetNotesDeFrais();
        return axios
            .put(
                this.apiUrl +
                    employeId +
                    "/note_de_frais/" +
                    date.format("YYYY-MM"),
                noteDeFrais
            )
            .then(function (response) {
                EmployeApi.forgetNotesDeFrais();
                return new NoteDeFrais(response.data.data);
            });
    }

    /**
     * La suppression envoie la note de frais à la corbeille : la raison est
     * obligatoire (cf. core/Modals/ConfirmSuppression).
     */
    removeNoteDeFrais(
        employeId: number,
        month: DateUtils.Month,
        raison: string
    ): Promise<NoteDeFrais> {
        let date = moment(month.year + "-" + month.month, "YYYY-MM");
        EmployeApi.forgetNotesDeFrais();
        return axios
            .delete(
                this.apiUrl +
                    employeId +
                    "/note_de_frais/" +
                    date.format("YYYY-MM"),
                { data: { raison: raison } }
            )
            .then(function (response) {
                EmployeApi.forgetNotesDeFrais();
                return new NoteDeFrais(response.data.data);
            });
    }

    // Options
    getAllAsOptions(params?: Object): Promise<Array<Option>> {
        return this.getAll(_.assign({ shared: true }, params)).then(function (
            res
        ) {
            return _.map(res, function (e) {
                return <Option>{
                    id: e.id,
                    name: e.fullName,
                };
            });
        });
    }
}
